import React, { useEffect, useState } from "react";
import Table from "../../common/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveContent,
  checkContentApproval,
  deleteContent,
  duplicateContent,
  getAllAdminArticles,
  hardDeleteContent,
  rejectContent,
} from "../../../store/api/content";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import { useNavigate, useSearchParams } from "react-router-dom";
import { columns } from "./columnsData";
import StatusModal from "../../common/Modal";
import Toast from "../../common/Toast";
import Button from "../../common/Button";

import PreviewContent from "./PreviewContent";
import { useOrganisation } from "../../../hooks/useOrganisation";
import { getRole } from "../../../helpers/auth";
import { getTextColor } from "../../../helpers";
import { roles } from "../../../constants/routes";
import CommonSearch from "../../common/Search";

const AllContentPage = ({ approved, schedule }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = getRole();
  const [searchParams] = useSearchParams();

  const { primaryColor } = useOrganisation();
  const companyName = localStorage.getItem("companyName");

  const textColor = getTextColor(primaryColor) || "black";

  const [searchTerm, setSearchTerm] = useState("");
  const [allContent, setAllContent] = useState([]);
  const [searchItem, setSearchItem] = useState("");
  const [totalPage, setTotalPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // New state for total items

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // New state for page size

  const [sortBy, setSortBy] = useState("DateCreated");
  const [tag, setTag] = useState("");

  const [isDescending, setIsDescending] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [contentId, setContentId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState({ message: false });
  const [responseMessage, setResponseMessage] = useState("");
  const [status, setStatus] = useState(200);
  const [openView, setOpenView] = useState(false);
  const [contentPreview, setContentPreview] = useState({});
  const [openArchive, setOpenArchive] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [statusContent, setStatusContent] = useState(null);
  const [SchedulDate, setSchedulDate] = useState(false);
  const [canApprove, setCanApprove] = useState(true);
  const [missingFields, setMissingFields] = useState([]);

  const { data, isLoading } = useQuery({
    queryKey: [
      "adminArticles",
      searchTerm,
      currentPage,
      pageSize,
      sortBy,
      isDescending,
      statusContent,
      approved,
      SchedulDate,
      tag,
    ],
    queryFn: () =>
      getAllAdminArticles({
        searchTerm,
        currentPage,
        sortBy,
        isDescending,
        status: statusContent,
        SchedulDate,
        tag,
        pageSize,
      }),
  });

  const { mutate: rejectMutate } = useMutation({
    mutationFn: () => rejectContent({ contentId, message }),
    onSuccess: (data) => {
      setMessage("");
      setStatus(200);
      setResponseMessage("Content Rejected");
      queryClient.refetchQueries({
        queryKey: ["adminArticles"], // Use an object with queryKey
      });
    },
    onError: (error) => {},
  });

  const { mutate: approveMutate } = useMutation({
    mutationFn: () => approveContent({ contentId }),
    onSuccess: (data) => {
      setMessage("");
      setStatus(200);
      setResponseMessage("Content Approved");
      queryClient.refetchQueries({
        queryKey: ["adminArticles"], // Use an object with queryKey
      });
    },
    onError: (error) => {},
  });

  const { mutate: archiveMutate } = useMutation({
    mutationFn: () =>
      deleteContent({ contentIds: [...selectedIds], hardDelete: false }),
    onSuccess: (data) => {
      setMessage("");
      setStatus(200);
      setResponseMessage("Content archived");
      queryClient.refetchQueries({
        queryKey: ["adminArticles"], // Use an object with queryKey
      });
      setSelectedIds([]);
    },
    onError: (error) => {},
  });

  const { mutate: massDeleteMutate } = useMutation({
    mutationFn: () =>
      deleteContent({ contentIds: [...selectedIds], hardDelete: true }),
    onSuccess: (data) => {
      setMessage("");
      setStatus(200);
      setResponseMessage("Content deleted permanently");
      queryClient.refetchQueries({
        queryKey: ["adminArticles"], // Use an object with queryKey
      });
      setSelectedIds([]);
    },
    onError: (error) => {
      setStatus(400);
      setResponseMessage("Error deleting content");
    },
  });

  const { mutate: checkApprovalMutate, isLoading: checkingApproval } =
    useMutation({
      mutationFn: (id) => checkContentApproval({ contentId: id }),
      onSuccess: (data) => {
        setCanApprove(data);
        setModalOpen(true);
      },
      onError: (error) => {
        setStatus(400);
        setResponseMessage("Error checking content approval status");
      },
    });

  const { mutate: deleteMutate } = useMutation({
    mutationFn: (id) => hardDeleteContent(id),
    onSuccess: () => {
      setResponseMessage("Content deleted successfully");
      setStatus(200);
      queryClient.refetchQueries(["adminArticles"]);
    },
    onError: () => {
      setResponseMessage("Error deleting content");
      setStatus(400);
    },
  });

  const { mutate: duplicateMutate } = useMutation({
    mutationFn: (id) => duplicateContent(id),
    onSuccess: (data) => {
      setResponseMessage("Content duplicated successfully");
      navigate(`/${companyName}/edit-content/${data}`);
    },
    onError: () => {
      setResponseMessage("Error duplicating content");
      setStatus(400);
    },
  });

  useEffect(() => {
    if (!isLoading) {
      setAllContent(data?.paginatedContent);
      setTotalPage(data.totalPages);
      setTotalItems(data?.totalItems || 0);
    }
  }, [isLoading, data]);

  useEffect(() => {
    if (approved) {
      setStatusContent(0);
    } else if (schedule) {
      setSchedulDate(true);
    } else {
      setStatusContent(null);
    }
  }, [approved]);

  useEffect(() => {
    const tagParam = searchParams.get("tag");
    if (tagParam) {
      setTag([tagParam]);
    }
  }, [searchParams]);

  const handleCloseView = () => setOpenView(false);
  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchItem(term);
  };
  const handleItemSearch = () => {
    setSearchTerm(searchItem);
  };
  const handleStatusChange = (id, status) => {
    setContentId(id);
    if (status === "Approved") {
      checkApprovalMutate(id);
    } else if (status === "Rejected") {
      setModalOpen(true);
    }
    setSelectedStatus(status);
  };
  const handleApproveORReject = () => {
    if (selectedStatus === "Rejected") {
      if (message) {
        rejectMutate();
        setModalOpen(false);
        setError({ ...error, message: false });
      } else {
        setError({ ...error, message: true });
      }
    } else if (selectedStatus === "Approved") {
      approveMutate();
      setModalOpen(false);
    }
  };
  const handleClose = () => {
    setModalOpen(false);
  };
  const handleSelect = (id) => {
    setSelectedIds((prevSelectedIds) =>
      prevSelectedIds.includes(id)
        ? prevSelectedIds.filter((selectedId) => selectedId !== id)
        : [...prevSelectedIds, id]
    );
  };
  const handleSelectAll = () => {
    if (selectedIds.length === allContent.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allContent.map((article) => article.id));
    }
  };
  const handleArchive = () => {
    archiveMutate();
    setOpenArchive(false);
  };
  const handleDeletePermanently = () => {
    massDeleteMutate();
    setOpenDelete(false);
  };
  const handleOpenArchiveDialog = () => {
    setOpenArchive(true);
  };
  const handleOpenDeleteDialog = () => {
    setOpenDelete(true);
  };
  const handleEditContent = () => {
    navigate(`/${companyName}/edit-content/${contentId}`);
    setModalOpen(false);
  };
  const handleCloseDelete = () => {
    setOpenDelete(false);
  };
  const handleCloseArchive = () => {
    setOpenArchive(false);
  };

  return (
    <>
      <Typography variant="h6" fontWeight={"bold"}>
        View all content
      </Typography>
      <Box>
        <Grid container flex={1} justifyContent={"space-between"}>
          <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
            <CommonSearch
              placeholder="Search content"
              onSearch={setSearchTerm}
              initialValue={searchTerm}
              sx={{ flexGrow: 1, marginRight: 2 }}
            />
            <Button
              endIcon={<ControlPointIcon />}
              onClick={() => navigate(`/${companyName}/create-content`)}
              text={"Add content"}
            />

            {roles.ContentCreator !== role && (
              <Button
                endIcon={<ControlPointIcon />}
                sx={{
                  backgroundColor: primaryColor,
                  color: textColor,
                  marginLeft: 2,
                }}
                onClick={() => navigate(`/${companyName}/Rss-Feed`)}
                text="Rss-Feed"
              />
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 2,
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={handleOpenArchiveDialog}
              backgroundColor="white"
              disabled={selectedIds.length === 0}
              text="Archive content"
            />
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={handleOpenDeleteDialog}
              backgroundColor="white"
              color="error"
              disabled={selectedIds.length === 0}
              text="Delete content"
            />
          </Box>
        </Grid>
      </Box>
      {/* Archive Dialog */}
      <Dialog
        open={openArchive}
        onClose={handleCloseArchive}
        aria-labelledby="archive-dialog-title"
        aria-describedby="archive-dialog-description"
      >
        <DialogTitle id="archive-dialog-title">
          Are you sure you want to archive the selected items?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="archive-dialog-description">
            Archived content will be hidden. You can restore this content at any
            time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseArchive}
            text="Cancel"
            backgroundColor="white"
          />
          <Button onClick={handleArchive} autoFocus text="Archive" />
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Are you sure you want to delete the selected items?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Deleted content cannot be restored. If you may wish to restore
            content in the future, please use the Archive feature instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDelete}
            text="Cancel"
            backgroundColor="white"
          />
          <Button
            onClick={handleDeletePermanently}
            autoFocus
            text="Delete"
            color="error"
          />
        </DialogActions>
      </Dialog>

      <Table
        data={allContent}
        columns={columns({
          selectedIds,
          handleSelect,
          handleSelectAll,
          handleStatusChange,
          setSortBy,
          sortBy,
          setIsDescending,
          isDescending,
          setOpenView,
          setContentPreview,
          approved,
          schedule,
          setStatusContent,
          statusContent,
          primaryColor,
          setCurrentPage,
          role,
          onDelete: (id) => deleteMutate(id),
          onDuplicate: (id) => duplicateMutate(id),
        })}
        totalPage={totalPage}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        isLoading={isLoading}
        isInitialLoading={isLoading}
        pageSize={pageSize}
        setPageSize={setPageSize}
        showPageSize={true}
      />
      <StatusModal
        open={modalOpen}
        status={selectedStatus}
        handleClose={handleClose}
        setMessage={setMessage}
        message={message}
        handleApproveORReject={handleApproveORReject}
        handleEditContent={handleEditContent}
        error={error}
        canApprove={canApprove}
        missingFields={missingFields}
      />

      <Toast message={responseMessage} status={status} />
      <PreviewContent
        open={openView}
        handleClose={handleCloseView}
        id={contentPreview.id}
      />
    </>
  );
};

export default AllContentPage;
